import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import {
  CheckboxField,
  createDecorator,
  DateField,
  Form,
  FormRenderProps,
  NumberField,
  PecFormApi,
  resetAllFieldStates,
  SubmitButton,
  TimeField,
} from 'components/form'
import { resolveValue } from 'components/form/core/hooks/useField'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { subDays } from 'date-fns'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { Fragment, useMemo, useState } from 'react'
import { dateAsYyyyMmDd, formatHoursMinutes, toDate } from 'util/date/formatDate'
import { onlyNumbersNonZeroLeft } from 'util/mask'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import { AtestadoContentBox } from '../components/AtestadoContentBox'
import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import { useAtestadoCancelarCadastro } from '../hooks/useAtestadoCancelarCadastro'
import { tituloAtestado } from '../utils-atestado'
import { createAtestadoPadraoCalculations } from './calculator-atestadoPadrao'
import { getAtestadoPadraoText } from './utils-atestadoPadrao'
import { atestadoPadraoFormValidator } from './validator-atestadoPadrao'

const name = metaPath<AtestadoModel>()

export interface AtestadoPadraoFormProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtestado
  dataAtendimento: Instant
  onSubmit(values: AtestadoModel): void
}

export function AtestadoPadraoForm(props: Readonly<AtestadoPadraoFormProps>) {
  const { atendimentoId, prontuarioId, cidadao, dataAtendimento, onSubmit } = props
  const { acesso } = useAcessoLotacaoOrEstagio()

  const nomeUnidadeSaude = acesso?.unidadeSaude.nome
  const possuiCpfCns = !!(cidadao.cpf || cidadao.cns)

  const decorators = useMemo(
    () => [createDecorator(...createAtestadoPadraoCalculations(name, cidadao, nomeUnidadeSaude))],
    [cidadao, nomeUnidadeSaude]
  )

  const alert = useAlert()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)
  const [isEmailCidadaoCanceled, setIsEmailCidadaoCanceled] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.PADRAO

  const initialValues = useMemo(
    () => ({ descricao: getAtestadoPadraoText({ cidadao, nomeUnidadeSaude }), tipo: tipoAtestado }),
    [cidadao, nomeUnidadeSaude, tipoAtestado]
  )

  const handleSubmit = (values: AtestadoModel, formApi: PecFormApi) => {
    onSubmit(values)
    setTimeout(formApi.reset)
    resetAllFieldStates(formApi)
    alert('success', 'Atestado salvo com sucesso.')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId: cidadao.id,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = useAtestadoCancelarCadastro({
    isEmailCidadaoDirty,
    setIsEmailCidadaoCanceled,
  })

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    const values = resolveValue(formProps.values, name) || ({} as AtestadoModel)

    return (
      <Fragment>
        {!possuiCpfCns && (
          <Cell size={8}>
            <Grid gap={1}>
              <Cell size={4}>
                <NumberField
                  name={name.cpfCns}
                  label='CPF/CNS'
                  disabled={values.naoPossui}
                  required={!values.naoPossui}
                  maxLength={15}
                />
              </Cell>
              <Cell>
                <HFlow style={styles.naoPossuiContainer}>
                  <CheckboxField name={name.naoPossui} label='Não possui' />
                </HFlow>
              </Cell>
            </Grid>
          </Cell>
        )}
        <Cell md={4} lg={3}>
          <DateField
            name={name.data.absolutePath()}
            label='Data'
            required
            initialValue={dateAsYyyyMmDd(dataAtendimento)}
            minDate={subDays(dataAtendimento, 90)}
            maxDate={toDate(dataAtendimento) ?? undefined}
          />
        </Cell>
        <Cell size={2}>
          <TimeField
            name={name.hora}
            label='Hora'
            placeholder='hh:mm'
            required
            initialValue={formatHoursMinutes(dataAtendimento)}
          />
        </Cell>
        <Cell size={2}>
          <NumberField name={name.dias} label='Dias' required maxLength={3} parse={onlyNumbersNonZeroLeft} />
        </Cell>
        <Cell size={4}>
          <CidSelectField name={name.cid10} label='CID10' renderGrupoCiapRelacionada={false} />
        </Cell>
        <AtestadoAssinaturaDigitalView
          name={name.assinadoDigitalmente}
          cidadaoId={cidadao.id}
          cidadaoEmail={emailCidadao}
          onEmailCidadaoChange={setIsSubmitDisabled}
          onDirtyChange={setIsEmailCidadaoDirty}
          hasCid10={!!formProps.values.cid10}
          style={styles.assinaturaDigital}
          emailCanceled={isEmailCidadaoCanceled}
          onEmailCancel={setIsEmailCidadaoCanceled}
        />
        <Cell size={12}>
          <VFlow>
            <AtestadoContentBox title={tituloAtestado(tipoAtestado)} content={values.descricao} />
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={() => handleCancel(formProps.form)}>
                Cancelar
              </Button>
              <Tooltip text={isSubmitDisabled ? MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED : undefined}>
                <SubmitButton
                  throttleSubmit
                  type='button'
                  size='small'
                  kind='primary'
                  handleSubmit={formProps.handleSubmit}
                  disabled={isSubmitDisabled || isAssinando}
                  loading={isAssinando}
                >
                  Salvar atestado
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </VFlow>
        </Cell>
        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Form<AtestadoModel>
      initialValues={initialValues}
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={atestadoPadraoFormValidator(possuiCpfCns, dateAsYyyyMmDd(dataAtendimento))}
      decorators={decorators}
    />
  )
}

const styles = {
  naoPossuiContainer: css`
    margin-top: 1.75rem;
  `,
  assinaturaDigital: css`
    padding-left: 1rem;
  `,
}
